/**
 * "Joining Request" card under Settings > User Management (Figma 10005:28685):
 * one row per pending request to join the current workspace, from the
 * Discover Workspaces page.  Each row shows the requester, "Requests to
 * join your workspace as an <role>" with the role picker (default Editor),
 * and Decline / Accept.  The card disappears when nothing is pending.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Avatar, Button, Divider, Text, sparkToast } from '@spark';
import { acceptJoinRequest, declineJoinRequest, fetchJoinRequests } from '@/features/workspaces/api/discoverApi';
import { roleWithArticle } from '@/features/workspaces/components/WorkspaceInvitationModal';
import UserRoleSelect, { INVITE_ROLE_OPTIONS } from './UserRoleSelect.jsx';

export const JOINING_REQUESTS_TITLE = 'Joining Request';
export const DEFAULT_JOIN_ROLE = 'Editor';
export const ACCEPT_LABEL = 'Accept';
export const DECLINE_LABEL = 'Decline';

const requesterName = (request) => (request.name || '').trim() || request.email;

/** "as an Editor" -> the article alone, so the role tag can follow as a picker. */
const articleFor = (role) => roleWithArticle(role).split(' ')[0];

function RequestRow({ request, role, busy, onRoleChange, onAccept, onDecline }) {
    const name = requesterName(request);
    return (
        <div className="joining-requests__row" data-testid="joining-request-row" data-request-id={request.id}>
            <div className="joining-requests__who">
                {request.picture ? (
                    <Avatar size="medium" type="image" src={request.picture} alt="" />
                ) : (
                    <Avatar size="medium" type="initials" primaryText={name} />
                )}
                <div className="joining-requests__text">
                    <Text as="span" variant="base-regular" className="joining-requests__name">
                        {name}
                    </Text>
                    {request.name ? (
                        <Text as="span" variant="base-regular" color="secondary" className="joining-requests__email">
                            ({request.email})
                        </Text>
                    ) : null}
                    <span className="joining-requests__ask-group">
                        <Text as="span" variant="base-regular" className="joining-requests__ask">
                            Requests to <strong>join your workspace</strong> as {articleFor(role)}
                        </Text>
                        <UserRoleSelect role={role} roles={INVITE_ROLE_OPTIONS} onChange={onRoleChange} />
                    </span>
                </div>
            </div>
            <div className="joining-requests__actions">
                <Button size="tiny" variant="tertiary" disabled={busy} onClick={onDecline} data-testid="joining-request-decline">
                    {DECLINE_LABEL}
                </Button>
                <Button size="tiny" variant="primary" disabled={busy} onClick={onAccept} data-testid="joining-request-accept">
                    {ACCEPT_LABEL}
                </Button>
            </div>
        </div>
    );
}

export default function JoiningRequestsSection({ workspaceId, onAccepted }) {
    const [requests, setRequests] = useState([]);
    const [roles, setRoles] = useState({});
    const [busyId, setBusyId] = useState(null);

    const load = useCallback(async () => {
        if (!workspaceId) return;
        const result = await fetchJoinRequests(workspaceId);
        // Non-admins (403) and vanished workspaces (404) simply have no card.
        setRequests(result.ok && Array.isArray(result.data?.requests) ? result.data.requests : []);
    }, [workspaceId]);

    useEffect(() => {
        load();
    }, [load]);

    const remove = (requestId) => setRequests((prev) => prev.filter((request) => request.id !== requestId));

    const handleAccept = async (request) => {
        const role = roles[request.id] || DEFAULT_JOIN_ROLE;
        setBusyId(request.id);
        const result = await acceptJoinRequest(workspaceId, request.id, role);
        setBusyId(null);
        if (result.ok) {
            remove(request.id);
            sparkToast.success('Request accepted', {
                detailed: true,
                content: `${requesterName(request)} joined your workspace as ${roleWithArticle(role)}.`,
            });
            onAccepted?.(request, role);
            return;
        }
        if (result.status === 404 || result.status === 409) {
            // Someone else already decided, or the request was withdrawn: refresh the card.
            remove(request.id);
            load();
        }
        sparkToast.error('Could not accept request', { detailed: true, content: result.error });
    };

    const handleDecline = async (request) => {
        setBusyId(request.id);
        const result = await declineJoinRequest(workspaceId, request.id);
        setBusyId(null);
        if (result.ok) {
            remove(request.id);
            sparkToast.success('Request declined', {
                detailed: true,
                content: `${requesterName(request)}'s request to join your workspace was declined.`,
            });
            return;
        }
        if (result.status === 404 || result.status === 409) {
            remove(request.id);
            load();
        }
        sparkToast.error('Could not decline request', { detailed: true, content: result.error });
    };

    if (requests.length === 0) return null;

    return (
        <section className="joining-requests" aria-label={JOINING_REQUESTS_TITLE} data-testid="joining-requests">
            <Text as="h3" variant="lead-medium" className="joining-requests__title">
                {JOINING_REQUESTS_TITLE}
            </Text>
            <div className="joining-requests__list">
                {requests.map((request, index) => (
                    <React.Fragment key={request.id}>
                        {index > 0 ? <Divider className="joining-requests__divider" /> : null}
                        <RequestRow
                            request={request}
                            role={roles[request.id] || DEFAULT_JOIN_ROLE}
                            busy={busyId === request.id}
                            onRoleChange={(role) => setRoles((prev) => ({ ...prev, [request.id]: role }))}
                            onAccept={() => handleAccept(request)}
                            onDecline={() => handleDecline(request)}
                        />
                    </React.Fragment>
                ))}
            </div>
        </section>
    );
}
